/**
 * 하이드레이션 불일치(React #418·#422·#423·#425) 판별과 원인 추적. (#262)
 *
 * ## 왜 따로 두는가
 *
 * 2026-08-21 `/info` 와 `/correct` 에서 React #418 이 잇달아 챗으로 올라왔다.
 * 스택이 전부 React 내부라 어느 자리가 어긋났는지 알 수 없었다. 확장 프로그램이
 * 없는 헤드리스 크로미움으로 운영과 로컬(dev)을 각각 열어 보니 두 곳 모두
 * 조용했다. 하이드레이션 직전에 본문 텍스트 노드를 바꿔치기하자 그때서야 똑같은
 * 메시지가 재현됐다(빈 `args[]=` 까지 알림에 찍힌 것과 같았다). 본문을 통째로
 * 갈아치우는 주체는 브라우저 자동 번역이다. 보고된 환경도 엣지(윈도우)와
 * 사파리(맥)로 갈렸다.
 *
 * 이 오류는 복구된다. React 가 서버 HTML 을 버리고 클라이언트에서 다시 그리므로
 * 사용자는 화면이 한 번 다시 그려지는 정도만 겪는다. 우리가 손댈 곳도 없다.
 * 그래서 번역 흔적이 남아 있으면 아예 신고하지 않고, 흔적이 없으면 우리 코드가
 * 원인일 수 있으니 `hydration` 스코프에 경고(⚠️)로 남긴다.
 */

/**
 * 운영 번들은 코드만 남기고(`Minified React error #418`), 개발 번들은 원문을
 * 낸다. 둘 다 잡는다.
 *
 * - 418: 서버가 그린 HTML 과 클라이언트 렌더가 다름
 * - 422·423: 하이드레이션 중 오류를 클라이언트 렌더로 복구함
 * - 425: 텍스트 내용 불일치
 */
const HYDRATION_MESSAGE =
  /Minified React error #(418|422|423|425)\b|Hydration failed because|error while hydrating|Text content does not match server-rendered HTML/i;

/** Error 객체·문자열 어느 쪽이 와도 하이드레이션 불일치인지 판별합니다. */
export function isHydrationMismatch(error: unknown): boolean {
  if (!error) return false;
  const message = String((error as { message?: string }).message ?? error);
  return HYDRATION_MESSAGE.test(message);
}

/**
 * 하이드레이션 전에 본문을 건드린 흔적을 찾습니다. 찾으면 이름, 없으면 null.
 *
 * 번역기는 손댄 자리에 표시를 남긴다. 표시가 있으면 불일치의 원인이 우리 코드가
 * 아니므로 신고를 건너뛴다. 사파리 번역처럼 표시를 안 남기는 것도 있어서, 못
 * 찾았다고 우리 버그라고 단정하지는 않는다. 그때는 경고로 남겨 두고 사람이 본다.
 */
export function findDomMutator(): string | null {
  if (typeof document === 'undefined') return null;

  try {
    const html = document.documentElement;

    // 마이크로소프트 번역기(엣지 내장)는 번역한 요소마다 해시 속성을 남긴다.
    if (document.querySelector('[_msttexthash], [_msthash]')) {
      return 'edge-translate';
    }

    // 구글 번역(크롬 내장·웹 위젯)은 <html> 에 방향 클래스를 붙인다.
    if (
      html.classList.contains('translated-ltr') ||
      html.classList.contains('translated-rtl')
    ) {
      return 'google-translate';
    }

    // 루트 레이아웃이 lang="ko" 로 못박아 두었다. 값이 바뀌어 있으면 번역기가
    // 문서를 옮긴 뒤다.
    const lang = html.getAttribute('lang');
    if (lang && lang.toLowerCase() !== 'ko') {
      return `translated:${lang.slice(0, 20)}`;
    }

    // 흔적을 안 남기는 번역(크롬 기기 내 번역·사파리)은 위 세 가지를 모두 비껴
    // 간다. 그래도 카나리의 본문 글자만은 바뀌므로, 속성에 심어 둔 원문과
    // 다르면 번역기가 본문을 갈아치운 것이다. 사용자가 읽는 언어가 한국어가
    // 아닐 때 이 경로로 들어온다.
    if (isCanaryAltered()) return 'canary-changed';

    return null;
  } catch {
    // 판별하다 실패하면 신고 쪽으로 보낸다. 놓치는 것보다 낫다.
    return null;
  }
}

/** 카나리 요소가 있고, 화면 글자가 속성에 심어 둔 원문과 다르면 true. */
function isCanaryAltered(): boolean {
  const canary = document.querySelector(`[${HYDRATION_CANARY_ATTR}]`);
  if (!canary) return false;
  return (
    (canary.textContent ?? '').trim() !==
    canary.getAttribute(HYDRATION_CANARY_ATTR)
  );
}

/**
 * 하이드레이션 카나리 — 번역기가 본문을 갈아치웠는지 증명하는 표본. (#262)
 *
 * 번역기는 **텍스트 노드만** 바꾸고 `data-*` 속성은 그대로 둔다. 그래서 같은
 * 문장을 속성과 본문에 각각 심어 두고 오류 시점에 둘을 대조하면, 흔적을 안
 * 남기는 번역기(사파리·최근 크롬 기기 내 번역)도 잡힌다.
 *
 * 화면에는 안 보이지만 DOM 에는 있어야 번역 대상이 된다(`sr-only`). 스크린리더
 * 에는 읽히지 않게 `aria-hidden` 을 함께 붙인다.
 */
export const HYDRATION_CANARY_ATTR = 'data-hydration-canary';

/** 카나리 문구. 짧고 평범한 한국어여야 번역기가 그냥 지나치지 않는다. */
export const HYDRATION_CANARY_TEXT = '바른 한국어 맞춤법 검사';

/** 값이 없거나 못 읽은 신호는 아예 담지 않는다. */
type Signals = Record<string, string>;

/**
 * 하이드레이션 불일치를 **신고할 때 같이 실어 보낼 판별 근거**를 모읍니다.
 *
 * `findDomMutator()` 가 흔적을 못 찾아도 원인이 번역기일 수 있다. 흔적 목록은
 * 브라우저 버전을 따라 늙기 때문이다(2026-09-08 안드로이드 크롬 152 가 세 흔적을
 * 모두 비껴가 ⚠️ 로 올라왔다). 그래서 필터를 더 세게 잡는 대신, 판단에 필요한
 * 값을 알림에 그대로 실어 사람이 가르게 한다.
 *
 * 여기서 신고 여부를 바꾸지 않는다 — 근거만 모은다.
 */
export function collectHydrationSignals(): Signals {
  const signals: Signals = {};
  if (typeof document === 'undefined') return signals;

  try {
    const html = document.documentElement;

    signals['html.lang'] = html.getAttribute('lang') ?? '(없음)';

    const className = html.className.trim();
    if (className) signals['html.class'] = className.slice(0, 200);

    // 엣지 번역기가 남기는 해시 속성. 몇 개나 붙었는지가 번역 범위를 보여 준다.
    const hashed = document.querySelectorAll(
      '[_msttexthash], [_msthash]'
    ).length;
    signals['번역 마커'] = hashed ? `edge ${hashed}개` : '없음';

    // 브라우저가 어느 언어로 옮기려 했는지 짐작할 단서.
    if (typeof navigator !== 'undefined') {
      const langs = navigator.languages?.slice(0, 3).join(',');
      signals['브라우저 언어'] = langs || navigator.language || '(없음)';
    }

    const canary = document.querySelector(`[${HYDRATION_CANARY_ATTR}]`);
    if (!canary) {
      signals['카나리'] = '요소 없음';
    } else {
      const original = canary.getAttribute(HYDRATION_CANARY_ATTR) ?? '';
      const shown = (canary.textContent ?? '').trim();
      signals['카나리'] =
        shown === original ? '원문 그대로' : `바뀜: ${shown.slice(0, 60)}`;
    }
  } catch (e) {
    signals['수집 실패'] = String((e as Error)?.message ?? e).slice(0, 120);
  }

  return signals;
}
